/**
 * Centralized query keys for React Query cache management
 * Each key should be unique and descriptive for optimal caching
 */
import { timelineAnalysisKey } from '@/domains/timeline/lib/query-keys';
import { isTimeRange } from '@/lib/time-range';
import type { FriendimizerFilters as FriendimizerFiltersInput } from '@/lib/validation/api';

// Filter types for admin user list queries
export interface AdminUsersFiltersQuery {
  activity?: string;
  joinedFrom?: string;
  joinedTo?: string;
  limit?: number;
  page?: number;
  role?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: string;
  verification?: string;
}

interface TweetFilters {
  includeReplies?: boolean;
  includeRetweets?: boolean;
  limit?: number;
  offset?: number;
}

// For search query keys we accept any filter shape used by callers
type SearchFilters = Record<string, unknown> | undefined;

type FriendimizerQueryFilters = FriendimizerFiltersInput & {
  includeFollowing?: boolean | undefined;
};

export const queryKeys = {
  // User data
  user: (id: string) => ['user', id] as const,
  userByUsername: (username: string) => ['user', 'by-username', username] as const,
  users: () => ['users'] as const,
  userSearch: (query: string) => ['users', 'search', query] as const,
  followStatus: (profileId: string) => ['user', profileId, 'follow-status'] as const,
  muteStatus: (profileId: string) => ['user', profileId, 'mute-status'] as const,
  // User analysis
  userAnalysis: (profileId: string) => ['user-analysis', profileId] as const,
  userAnalysisCache: (profileId: string) => ['user-analysis-cache', profileId] as const,

  // Admin
  adminUsers: (filters?: AdminUsersFiltersQuery) => ['admin', 'users', filters] as const,
  adminUserSearch: (filters: { query: string; limit?: number }) =>
    ['admin', 'users', 'search', filters.query, filters.limit ?? 10] as const,
  adminMissingUsers: () => ['admin', 'missing-users'] as const,
  adminFeatures: (userId: string) => ['admin', 'features', userId] as const,
  adminDatabaseStats: () => ['admin', 'database', 'stats'] as const,
  adminDatabasePing: () => ['admin', 'database', 'ping'] as const,

  // Tweets
  tweet: (id: string) => ['tweet', id] as const,
  tweetActions: (id: string) => ['tweet', id, 'actions'] as const,
  tweets: (userId: string, filters?: TweetFilters) => ['tweets', userId, filters] as const,
  tweetSearch: (query: string, filters?: SearchFilters) => ['tweets', 'search', query, filters] as const,

  // Timeline
  timeline: (timeRange: string) => ['timeline', timeRange] as const,
  timelineAnalysis: (timeRange: string) => timelineAnalysisKey(isTimeRange(timeRange) ? timeRange : '24h'),
  syncStatus: () => ['timeline', 'sync-status'] as const,

  // Cache
  timelineCache: (userId: string) => ['timeline-cache', userId] as const,

  // Auth
  oauthStatus: () => ['auth', 'oauth-status'] as const,
  adminAccessToken: () => ['admin', 'twitter', 'access-token'] as const,
  adminDelegatedApiKey: () => ['admin', 'api-keys', 'delegated'] as const,
  featureCheck: (featureId: string) => ['auth', 'feature-check', featureId] as const,

  // Friendimizer
  friendimizerData: (filters?: FriendimizerQueryFilters) => ['friendimizer', 'data', filters] as const,
  friendimizerImpact: (filters?: FriendimizerQueryFilters) => ['friendimizer', 'impact', filters] as const,
  friendimizerStats: () => ['friendimizer', 'stats'] as const,
  friendimizerRecommendations: () => ['friendimizer', 'recommendations'] as const,
  exploreNetworkCoverage: (userId: string) => ['explore', 'network-coverage', userId] as const,
} as const;
